import type { LayerKind } from "@/layers/utility/createLayerObject";
import { AddParameterForm } from "@/overlay/components/WeatherParameterForm/AddParameterForm";
import { FormField, type FormFieldProps } from "@/overlay/components/form-field";
import { Modal } from "@/overlay/components/window/Modal";
import { classNames } from "@/utility/jsx";
import { searchEnginesWeatherParams, useSearchEngineReady } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  type ExactMatchResult,
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  autofill,
} from "@mm/weather-parameter-utils";
import { MmButton } from "@mm/zeus-react";
import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";

export type ParameterRemoveCallback = () => void;

export interface BasicWeatherParameterFormProps {
  parameterStr?: string;
  /**
   * A string of layer description used to hide particular options that are unavailable.
   */
  layerKind?: LayerKind;
  /**
   * Whether to prefill all fields with a default value, even if the field was not yet narrowed by the user.
   */
  autofill?: boolean;
  showExactParameter: boolean;
  /**
   * A callback for when the user clicks on the parameter edit button. If given, it will show a parameter edit button.
   * To get the configured string format (ex.: t_10m:K), use `parameterToString(param)?.formatted`
   */
  onSubmitParam?: (param: HasLayerIdxNarrowing<PartialWeatherParameter>) => void;
  /**
   * A callback for when the user clicks on the parameter remove button. If given, it will show a parameter remove button.
   */
  onRemoveParameter?: ParameterRemoveCallback;
  parameterFormFieldProps?: Partial<FormFieldProps>;
}

/**
 * An inline form to allow a user to select and edit the weather parameter's options.
 */
export function BasicWeatherParameterForm(props: BasicWeatherParameterFormProps) {
  // Manage local parameter state
  const [localParameter, setLocalParameter] = useState<HasLayerIdxNarrowing<PartialWeatherParameter> | null>(null);
  const [showParamForm, setShowParamForm] = useState(false);

  const handleSubmitParam = (updatedParam: HasLayerIdxNarrowing<PartialWeatherParameter>) => {
    const newCurrentParam = autofill(updatedParam);
    if (props.onSubmitParam) {
      props.onSubmitParam(newCurrentParam);
    }
    // Close the AddParameterForm after submission
    setShowParamForm(false);
  };
  const searchEngineReady = useSearchEngineReady();

  useEffect(() => {
    let isSubscribed = true;
    if (!localParameter && searchEngineReady) {
      if (!searchEnginesWeatherParams.standard || !props.parameterStr) {
        return;
      }

      searchEnginesWeatherParams.standard
        .search(props.parameterStr)
        .then((response) => {
          if (isSubscribed) {
            if (response.results?.regular?.[0]) {
              const result: ExactMatchResult = response.results.regular[0];
              setLocalParameter(
                autofill({
                  parameter: result.layerGroup,
                  narrowed_selection: {
                    layerIdx: result.matchingLayers[0].layerIdx,
                    // biome-ignore lint/style/noNonNullAssertion: TODO this is becoming unacceptable
                    fields: result.matchingLayers[0].match.groups!,
                  },
                }),
              );
            } else {
              console.warn(`Search for parameter ${localParameter} errored out.`);
            }
          }
        })
        .catch((e) => {
          console.warn(`Search for parameter close to ${localParameter} errored out.`);
        });
    }
    return () => {
      isSubscribed = false;
    };
  }, [props.parameterStr, localParameter, searchEngineReady]);

  return (
    <div className={styles.basicFormContainer}>
      {/* Display exact parameter string if enabled */}
      {props.showExactParameter && (
        <FormField label={t`Parameter`} {...props.parameterFormFieldProps}>
          <div className={"editable-variable"}>
            {props.parameterStr ? (
              <span
                className={styles["parameter-name-span"]}
                // Title attribute shows the full parameter name in case it's too long and cut off at the end.
                title={props.parameterStr}
              >
                {props.parameterStr}
              </span>
            ) : (
              <MmButton size={"small"} type={"secondary"} onClick={() => setShowParamForm(true)}>
                <Trans>Add Parameter</Trans>
              </MmButton>
            )}
            <div className={"editable-variable__actions"}>
              {props.onSubmitParam && localParameter ? (
                <span
                  onClick={() => setShowParamForm(true)}
                  className="material-icons editable-variable__edit"
                  role="button"
                  aria-label="Edit Parameter"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      props.onSubmitParam?.(localParameter);
                    }
                  }}
                >
                  edit
                </span>
              ) : null}
              {props.onRemoveParameter && (
                <span
                  onClick={props.onRemoveParameter}
                  className={classNames("material-icons editable-variable__edit")}
                  role="button"
                  aria-label="Remove Parameter"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      props.onRemoveParameter?.();
                    }
                  }}
                >
                  delete
                </span>
              )}
            </div>
          </div>
        </FormField>
      )}
      <Modal isOpen={showParamForm} onClose={() => setShowParamForm(false)}>
        <AddParameterForm
          onSubmitParam={handleSubmitParam}
          onCancel={() => setShowParamForm(false)}
          source="edit-param"
        />
      </Modal>
    </div>
  );
}
